import React, { useState, useEffect, useRef } from "react";
import "../../styles/index.css";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Dialog,
  DialogContent,
  TextField,
  Radio,
  Tabs,
  Tab,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import EditSquareIcon from "@mui/icons-material/EditSquare";
import AddIcon from "@mui/icons-material/Add";
import BASE_URL from "../../components/config/appconfig";
import axios from "axios";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import SnackbarAlert from "../snackbarAlert/snackbarAlert";
import CircularProgress from "@mui/material/CircularProgress";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import renderOptionText from "../../utils/renderOptionText";

const MAX_SECTIONS = 3;
const MAX_SUBSECTIONS_PER_SECTION = [3, 4, 3]; // Section 1, 2, 3
const MAX_QUESTIONS_PER_SUBSECTION = 4; // 3 objective + 1 reflection
const OBJECTIVE_QUESTIONS_COUNT = 3;

const QuestionEditor = () => {
  const [sectionsData, setSectionsData] = useState([]);
  const [openSection, setOpenSection] = useState(null);
  const [openSubsection, setOpenSubsection] = useState(null);
  const [addQuestionOpen, setAddQuestionOpen] = useState(false);

  // Unified form for both objective and reflection questions
  const [questionForm, setQuestionForm] = useState({
    question_text: "",
    options: [
      { option_text: "" },
      { option_text: "" },
      { option_text: "" },
      { option_text: "" },
      { option_text: "" },
    ],
  });

  // Tracks which type the current add/edit dialog is for: "objective" | "reflection"
  const [currentQuestionType, setCurrentQuestionType] = useState("objective");

  const [selectedSubsectionId, setSelectedSubsectionId] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteSection, setDeleteSection] = useState(null);
  const [deleteQuestionOpen, setDeleteQuestionOpen] = useState(false);
  const [selectedQuestionToDelete, setSelectedQuestionToDelete] =
    useState(null);
  const [editQuestionMode, setEditQuestionMode] = useState(false);

  // Single question ID string (not the old paired { objectiveId, reflectionId } object)
  const [editingQuestionId, setEditingQuestionId] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [loading, setLoading] = useState(true);

  // Per-action loading states for button spinners
  const [savingQuestion, setSavingQuestion] = useState(false);
  const [submittingSection, setSubmittingSection] = useState(false);
  const [deletingSection, setDeletingSection] = useState(false);
  const [deletingQuestion, setDeletingQuestion] = useState(false);

  const [dialogTab, setDialogTab] = useState(0);
  const [focusedOptionIndex, setFocusedOptionIndex] = useState(null);

  const optionRefs = useRef([]);

  const applyFormat = (wrap) => {
    const index = focusedOptionIndex;
    if (index === null) {
      showSnackbar("Click into an option field first, then select text to format", "info");
      return;
    }
    const input = optionRefs.current[index];
    if (!input) return;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    if (start === end) {
      showSnackbar("Select text in the option field first, then click a format button", "info");
      return;
    }
    const text = questionForm.options[index].option_text;
    const [open, close] = wrap;
    const newText = text.slice(0, start) + open + text.slice(start, end) + close + text.slice(end);
    const updated = [...questionForm.options];
    updated[index].option_text = newText;
    setQuestionForm((prev) => ({ ...prev, options: updated }));
  };

  const handleBoldOption      = () => applyFormat(["**", "**"]);
  const handleItalicOption    = () => applyFormat(["_", "_"]);
  const handleUnderlineOption = () => applyFormat(["~~", "~~"]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    buildSectionsData();
  }, []);

  const buildSectionsData = async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      const sectionRes = await axios.get(`${BASE_URL}/api/sections`);

      if (!sectionRes.data.success) {
        setLoading(false);
        return;
      }

      const sections = sectionRes.data.data.sort(
        (a, b) => a.display_order - b.display_order,
      );

      const finalSections = [];

      for (const section of sections) {
        const subRes = await axios.get(
          `${BASE_URL}/api/subsections/sections-id/${section._id}`,
        );

        const subsections = subRes.data.data.sort(
          (a, b) => a.display_order - b.display_order,
        );

        const formattedSubsections = [];

        for (const sub of subsections) {
          const qRes = await axios.get(
            `${BASE_URL}/api/questions/subsection/${sub._id}`,
          );

          const questions = qRes.data.data || [];

          // Flatten each API question record into individual display items:
          // objective and reflection become separate entries in the list.
          const formattedQuestions = [];
          for (const q of questions) {
            if (q.objective) {
              formattedQuestions.push({
                id: q.objective._id,
                type: "objective",
                question_id: q.objective._id,
                question_text: q.objective.question_text || "",
                options: (q.objective.question_options || []).sort(
                  (a, b) =>
                    (a.display_order ?? a.option_value ?? 0) -
                    (b.display_order ?? b.option_value ?? 0),
                ),
                display_order: q.objective.display_order,
              });
            }
            if (q.reflection) {
              formattedQuestions.push({
                id: q.reflection._id,
                type: "reflection",
                question_id: q.reflection._id,
                question_text: q.reflection.question_text || "",
                options: [],
                display_order: q.reflection.display_order,
              });
            }
          }

          // Sort by display_order so objectives come first, reflection last
          formattedQuestions.sort((a, b) => a.display_order - b.display_order);

          const objectiveCount = formattedQuestions.filter(
            (q) => q.type === "objective",
          ).length;
          const reflectionCount = formattedQuestions.filter(
            (q) => q.type === "reflection",
          ).length;

          formattedSubsections.push({
            id: sub._id,
            title: sub.name,
            objectiveCount,
            reflectionCount,
            questions: formattedQuestions,
          });
        }

        finalSections.push({
          id: section._id,
          title: section.name,
          subsections: formattedSubsections,
        });
      }

      setSectionsData(finalSections);
    } catch (error) {
      console.error("Error building sections:", error);
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  const handleAddOption = () => {
    if (questionForm.options.length >= 5) {
      showSnackbar("Maximum 5 options allowed.", "error");
      return;
    }
    setQuestionForm((prev) => ({
      ...prev,
      options: [...prev.options, { option_text: "" }],
    }));
  };

  const handleRemoveOption = (index) => {
    const updated = questionForm.options.filter((_, i) => i !== index);
    setQuestionForm((prev) => ({ ...prev, options: updated }));
  };

  const handleOptionChange = (index) => (e) => {
    const updated = [...questionForm.options];
    updated[index].option_text = e.target.value;
    setQuestionForm((prev) => ({ ...prev, options: updated }));
  };

  const handleOpenDeleteQuestion = (subsectionId, question) => {
    setSelectedQuestionToDelete({ subsectionId, question });
    setDeleteQuestionOpen(true);
  };

  const handleCloseDeleteQuestion = () => {
    setDeleteQuestionOpen(false);
    setSelectedQuestionToDelete(null);
  };

  const handleConfirmDeleteQuestion = async () => {
    try {
      if (!selectedQuestionToDelete) return;
      setDeletingQuestion(true);
      const { question } = selectedQuestionToDelete;

      // Delete by single question ID (separate objective or reflection)
      await axios.delete(
        `${BASE_URL}/api/questions/delete/${question.question_id}`,
      );

      showSnackbar("Question deleted successfully");
      await buildSectionsData(false);
      handleCloseDeleteQuestion();
    } catch (error) {
      console.error("Delete question error:", error);
      showSnackbar("Failed to delete question. Please try again.", "error");
    } finally {
      setDeletingQuestion(false);
    }
  };

  // Add Section popup state
  const [addSectionOpen, setAddSectionOpen] = useState(false);
  const [sectionForm, setSectionForm] = useState({
    sectionName: "",
    subsections: [
      { id: null, name: "" },
      { id: null, name: "" },
      { id: null, name: "" },
    ],
  });

  const toggleSection = (sectionId) => {
    setOpenSection((prev) => (prev === sectionId ? null : sectionId));
  };

  const toggleSubsection = (sectionId, subsectionId) => {
    const key = `${sectionId}-${subsectionId}`;
    setOpenSubsection((prev) => (prev === key ? null : key));
  };

  const handleOpenAddQuestion = (subsectionId) => {
    const section = sectionsData.find((s) =>
      s.subsections.some((sub) => sub.id === subsectionId),
    );
    const subsection = section?.subsections.find(
      (sub) => sub.id === subsectionId,
    );

    if (!subsection) return;

    // Requirement 6: block at 4 questions
    if (subsection.questions.length >= MAX_QUESTIONS_PER_SUBSECTION) {
      showSnackbar(
        `Each subsection can have a maximum of ${MAX_QUESTIONS_PER_SUBSECTION} questions (${OBJECTIVE_QUESTIONS_COUNT} Objective + 1 Reflection).`,
        "error",
      );
      return;
    }

    const objectivesDone = subsection.questions.filter(
      (q) => q.type === "objective",
    ).length;
    const reflectionDone = subsection.questions.filter(
      (q) => q.type === "reflection",
    ).length;

    if (objectivesDone < OBJECTIVE_QUESTIONS_COUNT) {
      setCurrentQuestionType("objective");
    } else if (reflectionDone === 0) {
      setCurrentQuestionType("reflection");
    } else {
      showSnackbar(
        `Each subsection can have a maximum of ${MAX_QUESTIONS_PER_SUBSECTION} questions (${OBJECTIVE_QUESTIONS_COUNT} Objective + 1 Reflection).`,
        "error",
      );
      return;
    }

    setSelectedSubsectionId(subsectionId);
    setQuestionForm({
      question_text: "",
      options: [
        { option_text: "" },
        { option_text: "" },
        { option_text: "" },
        { option_text: "" },
        { option_text: "" },
      ],
    });
    setEditQuestionMode(false);
    setEditingQuestionId(null);
    setDialogTab(0);
    setAddQuestionOpen(true);
  };

  const handleEditQuestion = (subsectionId, question) => {
    setSelectedSubsectionId(subsectionId);
    setCurrentQuestionType(question.type);

    setQuestionForm({
      question_text: question.question_text || "",
      options:
        question.type === "objective" &&
        question.options &&
        question.options.length > 0
          ? question.options.map((opt) => ({ option_text: opt.option_text }))
          : [
              { option_text: "" },
              { option_text: "" },
              { option_text: "" },
              { option_text: "" },
              { option_text: "" },
            ],
    });

    setEditingQuestionId(question.question_id);
    setEditQuestionMode(true);
    setDialogTab(0);
    setAddQuestionOpen(true);
  };

  const handleCloseAddQuestion = () => {
    setAddQuestionOpen(false);
    setEditQuestionMode(false);
    setEditingQuestionId(null);
    setDialogTab(0);
    setFocusedOptionIndex(null);
  };

  const handleFormChange = (field) => (e) => {
    setQuestionForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSaveQuestion = async () => {
    try {
      setSavingQuestion(true);
      if (!questionForm.question_text.trim()) {
        showSnackbar(
          `${currentQuestionType === "objective" ? "Objective" : "Reflection"} question text is required`,
          "error",
        );
        return;
      }

      const section = sectionsData.find((s) =>
        s.subsections.some((sub) => sub.id === selectedSubsectionId),
      );
      const subsection = section.subsections.find(
        (sub) => sub.id === selectedSubsectionId,
      );

      // Preserve display_order when editing, assign next when adding
      let currentDisplayOrder = subsection.questions.length + 1;
      if (editQuestionMode && editingQuestionId) {
        const editingQ = subsection.questions.find(
          (q) => q.question_id === editingQuestionId,
        );
        if (editingQ) currentDisplayOrder = editingQ.display_order;
      }

      if (currentQuestionType === "objective") {
        const validOptions = questionForm.options.filter(
          (opt) => opt.option_text.trim() !== "",
        );
        if (validOptions.length < 2) {
          showSnackbar("Minimum 2 options required", "error");
          return;
        }

        const objectivePayload = {
          question_text: questionForm.question_text,
          display_order: currentDisplayOrder,
          question_options: validOptions.map((opt, index) => ({
            option_text: opt.option_text,
            option_value: index + 1,
          })),
        };

        if (editQuestionMode) {
          // Update objective question only
          await axios.put(
            `${BASE_URL}/api/questions/update/${editingQuestionId}/null`,
            { objective: objectivePayload },
          );
        } else {
          // Create objective question only
          await axios.post(`${BASE_URL}/api/questions`, {
            subsection_id: selectedSubsectionId,
            objective_question: objectivePayload,
          });
        }
      } else {
        // Reflection question
        const reflectionPayload = {
          question_text: questionForm.question_text,
          display_order: currentDisplayOrder,
        };

        if (editQuestionMode) {
          // Update reflection question only
          await axios.put(
            `${BASE_URL}/api/questions/update/null/${editingQuestionId}`,
            { reflection: reflectionPayload },
          );
        } else {
          // Create reflection question only
          await axios.post(`${BASE_URL}/api/questions`, {
            subsection_id: selectedSubsectionId,
            reflection_question: reflectionPayload,
          });
        }
      }

      showSnackbar(
        editQuestionMode
          ? "Question updated successfully"
          : "Question created successfully",
      );

      await buildSectionsData(false);
      setAddQuestionOpen(false);
      setEditQuestionMode(false);
      setEditingQuestionId(null);
    } catch (error) {
      console.error("Save question error:", error);
      showSnackbar("Failed to save question. Please try again.", "error");
    } finally {
      setSavingQuestion(false);
    }
  };

  // Add Section handlers
  const handleOpenAddSection = () => {
    if (sectionsData.length >= 3) {
      showSnackbar("Maximum 3 sections allowed.", "error");
      return;
    }
    setEditMode(false);
    setEditingSectionId(null);
    setSectionForm({
      sectionName: "",
      subsections: [
        { id: null, name: "" },
        { id: null, name: "" },
        { id: null, name: "" },
      ],
    });
    setAddSectionOpen(true);
  };

  const handleCloseAddSection = () => {
    setAddSectionOpen(false);
  };

  const handleOpenDelete = (section) => {
    setDeleteSection(section);
    setDeleteOpen(true);
  };

  const handleCloseDelete = () => {
    setDeleteOpen(false);
    setDeleteSection(null);
  };

  const handleConfirmDelete = async () => {
    try {
      if (!deleteSection) return;
      setDeletingSection(true);
      await axios.delete(`${BASE_URL}/api/sections/delete/${deleteSection.id}`);
      showSnackbar("Section deleted successfully.");
      await buildSectionsData(false);
      setDeleteOpen(false);
      setDeleteSection(null);
    } catch (error) {
      console.error("Delete section error:", error);
      showSnackbar("Failed to delete section. Please try again.", "error");
    } finally {
      setDeletingSection(false);
    }
  };

  const handleSectionFormChange = (field) => (e) => {
    setSectionForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubsectionChange = (index) => (e) => {
    const updated = [...sectionForm.subsections];
    updated[index].name = e.target.value;
    setSectionForm((prev) => ({ ...prev, subsections: updated }));
  };

  const handleAddSubsection = () => {
    const sectionIndex = editMode
      ? sectionsData.findIndex((s) => s.id === editingSectionId)
      : sectionsData.length;
    const maxSubs = MAX_SUBSECTIONS_PER_SECTION[sectionIndex] ?? 3;
    if (sectionForm.subsections.length >= maxSubs) {
      showSnackbar(
        `Section ${sectionIndex + 1} can have a maximum of ${maxSubs} subsections.`,
        "error",
      );
      return;
    }
    setSectionForm((prev) => ({
      ...prev,
      subsections: [...prev.subsections, { id: null, name: "" }],
    }));
  };

  const handleRemoveSubsection = (index) => {
    const updated = sectionForm.subsections.filter((_, i) => i !== index);
    setSectionForm((prev) => ({ ...prev, subsections: updated }));
  };

  const handleSubmitSection = async () => {
    try {
      setSubmittingSection(true);
      if (!sectionForm.sectionName.trim()) {
        showSnackbar("Section name is required", "error");
        return;
      }

      const validSubsections = sectionForm.subsections.filter(
        (s) => s.name.trim() !== "",
      );

      const sectionIndex = editMode
        ? sectionsData.findIndex((s) => s.id === editingSectionId)
        : sectionsData.length;
      const requiredSubs = MAX_SUBSECTIONS_PER_SECTION[sectionIndex] ?? 3;

      if (validSubsections.length !== requiredSubs) {
        showSnackbar(
          `Section ${sectionIndex + 1} must have exactly ${requiredSubs} subsections.`,
          "error",
        );
        return;
      }

      if (editMode) {
        await axios.put(`${BASE_URL}/api/sections/update/${editingSectionId}`, {
          name: sectionForm.sectionName,
        });

        for (let i = 0; i < validSubsections.length; i++) {
          const sub = validSubsections[i];
          if (sub.id) {
            await axios.put(`${BASE_URL}/api/subsections/update/${sub.id}`, {
              name: sub.name,
              display_order: i + 1,
            });
          } else {
            await axios.post(`${BASE_URL}/api/subsections`, {
              name: sub.name,
              section_id: editingSectionId,
              display_order: i + 1,
            });
          }
        }
      } else {
        const nextDisplayOrder = sectionsData.length + 1;
        const sectionRes = await axios.post(`${BASE_URL}/api/sections`, {
          name: sectionForm.sectionName,
          display_order: nextDisplayOrder,
        });

        const createdSection = sectionRes.data.data[0];
        const sectionId = createdSection.id || createdSection._id;

        for (let i = 0; i < validSubsections.length; i++) {
          await axios.post(`${BASE_URL}/api/subsections`, {
            name: validSubsections[i].name,
            section_id: sectionId,
            display_order: i + 1,
          });
        }
      }

      await buildSectionsData(false);
      setAddSectionOpen(false);
      setEditMode(false);
      setEditingSectionId(null);
      setSectionForm({
        sectionName: "",
        subsections: [
          { id: null, name: "" },
          { id: null, name: "" },
          { id: null, name: "" },
        ],
      });
      showSnackbar(
        editMode
          ? "Section updated successfully"
          : "Section created successfully",
      );
    } catch (error) {
      console.error("Error saving section:", error);
      showSnackbar("Failed to save section. Please try again.", "error");
    } finally {
      setSubmittingSection(false);
    }
  };

  const handleEditSection = (section) => {
    setEditMode(true);
    setEditingSectionId(section.id);
    setSectionForm({
      sectionName: section.title,
      subsections: section.subsections.map((s) => ({
        id: s.id,
        name: s.title,
      })),
    });
    setAddSectionOpen(true);
  };

  const totalSubsections = sectionsData.reduce(
    (acc, s) => acc + s.subsections.length,
    0,
  );
  const totalQuestions = sectionsData.reduce(
    (acc, s) =>
      acc + s.subsections.reduce((a, sub) => a + sub.questions.length, 0),
    0,
  );

  if (loading) {
    return (
      <Box
        sx={{
          textAlign: "center",
          mt: 6,
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Typography variant="body1">
          Please wait while we load your question editor...
        </Typography>
        <CircularProgress size={30} />
      </Box>
    );
  }

  return (
    <Box className="editor-list-container">
      {/* Card */}
      <Box className="editor-card">
        <Typography className="editor-title">
          {sectionsData.length} sections • {totalSubsections} subsections •{" "}
          {totalQuestions} questions
        </Typography>

        {sectionsData.length === 0 && (
          <Box className="editor-empty-state">
            <Typography variant="h4" className="editor-empty-state-title">
              No sections yet
            </Typography>
            <Typography className="editor-empty-state-subtitle">
              Build your questionnaire by adding sections, subsections, and
              questions.
            </Typography>

            {/* Stepper */}
            <Box className="editor-stepper">
              <Box className="step-item active">
                <Box className="step-number">
                  <Typography>1</Typography>
                </Box>
                <Typography className="step-text">Add a section</Typography>
              </Box>
              <ArrowForwardIcon className="step-arrow" />
              <Box className="step-item">
                <Box className="step-number">
                  <Typography>2</Typography>
                </Box>
                <Typography className="step-text">Add a sub-section</Typography>
              </Box>
              <ArrowForwardIcon className="step-arrow" />
              <Box className="step-item">
                <Box className="step-number">
                  <Typography>3</Typography>
                </Box>
                <Typography className="step-text">
                  Add your questions
                </Typography>
              </Box>
            </Box>

            {/* Dashed Add Section Box */}
            <Box
              className="add-section-dashed-box"
              onClick={handleOpenAddSection}
            >
              <Box className="dashed-box-content">
                <AddIcon sx={{ fontSize: 20 }} />
                <Typography className="dashed-box-text">
                  Add new section
                </Typography>
              </Box>
            </Box>

            {/* Info Cards */}
            <Box className="editor-info-cards">
              <Box className="info-card">
                <Box className="info-card-icon-box">
                  <AssignmentOutlinedIcon sx={{ fontSize: 32 }} />
                </Box>
                <Box className="info-card-content">
                  <Typography className="info-card-title">
                    Sections organise your content
                  </Typography>
                  <Typography className="info-card-desc">
                    Use sections as broad topic areas. Each section can hold
                    multiple subsection for finer grouping.
                  </Typography>
                </Box>
              </Box>

              <Box className="info-card">
                <Box className="info-card-icon-box">
                  <ListAltOutlinedIcon sx={{ fontSize: 32 }} />
                </Box>
                <Box className="info-card-content">
                  <Typography className="info-card-title">
                    Subsections adds structure
                  </Typography>
                  <Typography className="info-card-desc">
                    Break each section into focused categories. Subsections make
                    long questionnaires easier to navigate.
                  </Typography>
                </Box>
              </Box>

              <Box className="info-card">
                <Box className="info-card-icon-box">
                  <DriveFileRenameOutlineOutlinedIcon sx={{ fontSize: 32 }} />
                </Box>
                <Box className="info-card-content">
                  <Typography className="info-card-title">
                    Questions are your data points
                  </Typography>
                  <Typography className="info-card-desc">
                    Add Ratings for quick answers and Filling questions for
                    detailed insights.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/* Render each section */}
        {sectionsData.map((section, index) => {
          const isSectionOpen = openSection === section.id;

          return (
            <Box key={section.id} className="editor-content-box">
              {/* SECTION ROW */}
              <Box className="editor-section-row">
                <Box className="editor-section-left">
                  <IconButton
                    onClick={() => toggleSection(section.id)}
                    className="editor-arrow-btn"
                  >
                    {isSectionOpen ? (
                      <KeyboardArrowUpIcon />
                    ) : (
                      <KeyboardArrowDownIcon />
                    )}
                  </IconButton>

                  <Button variant="contained" className="section-btn">
                    Section {index + 1}
                  </Button>

                  <Typography className="editor-content-title">
                    {section.title}
                  </Typography>

                  <Typography className="editor-content-subtitle">
                    ({section.subsections.length} subsections)
                  </Typography>
                </Box>

                <Box className="editor-section-right">
                  <IconButton onClick={() => handleEditSection(section)}>
                    <EditSquareIcon className="editor-action-icon" />
                  </IconButton>
                  <IconButton onClick={() => handleOpenDelete(section)}>
                    <DeleteOutlinedIcon className="editor-action-icon" />
                  </IconButton>
                </Box>
              </Box>

              {/* SUBSECTIONS (shown when section is open) */}
              {isSectionOpen &&
                section.subsections.map((sub) => {
                  const subKey = `${section.id}-${sub.id}`;
                  const isSubOpen = openSubsection === subKey;

                  return (
                    <Box key={sub.id} className="editor-subsection-wrapper">
                      {/* Subsection header row */}
                      <Box
                        className={`editor-subsection-row ${
                          isSubOpen ? "editor-subsection-row-active" : ""
                        }`}
                      >
                        <Box className="editor-subsection-left">
                          <IconButton
                            onClick={() => toggleSubsection(section.id, sub.id)}
                            className={`editor-arrow-btn ${
                              isSubOpen ? "editor-arrow-btn-white" : ""
                            }`}
                          >
                            {isSubOpen ? (
                              <KeyboardArrowUpIcon />
                            ) : (
                              <KeyboardArrowDownIcon />
                            )}
                          </IconButton>

                          <Typography
                            className={`editor-subsection-title ${
                              isSubOpen ? "editor-subsection-title-active" : ""
                            }`}
                          >
                            {sub.title}
                          </Typography>

                          <Typography
                            className={`editor-subsection-subtitle ${
                              isSubOpen
                                ? "editor-subsection-subtitle-active"
                                : ""
                            }`}
                          >
                            {isSubOpen
                              ? `(${sub.objectiveCount} Objective & ${sub.reflectionCount} Self-Reflection Questions)`
                              : `(${sub.questions.length} Questions)`}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Questions (shown when subsection is open) */}
                      {isSubOpen && (
                        <>
                          {sub.questions.length > 0 ? (
                            <Box className="editor-questions-container">
                              {/* Individual question cards — one per question */}
                              {sub.questions.map((q, qIndex) => (
                                <Box
                                  key={q.id}
                                  className="editor-question-card"
                                >
                                  <Box className="editor-question-content">
                                    <Typography
                                      className={`editor-question-label${
                                        q.type === "reflection"
                                          ? " editor-question-label-reflection"
                                          : ""
                                      }`}
                                    >
                                      {q.type === "objective"
                                        ? "Objective"
                                        : "Self-Reflection"}
                                    </Typography>
                                    <Typography className="editor-question-text">
                                      {qIndex + 1}. {q.question_text}
                                    </Typography>
                                    {q.type === "objective" &&
                                      q.options &&
                                      q.options.length > 0 && (
                                        <Box
                                          sx={{
                                            mt: 1,
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 0.5,
                                            pl: 0.5,
                                          }}
                                        >
                                          {q.options.map((opt, i) => (
                                            <Box
                                              key={i}
                                              sx={{
                                                display: "flex",
                                                alignItems: "flex-start",
                                                gap: 1,
                                              }}
                                            >
                                              <Radio
                                                size="small"
                                                disabled
                                                sx={{
                                                  color: "#90a4ae",
                                                  p: 0,
                                                  mt: "2px",
                                                  flexShrink: 0,
                                                  "& .MuiSvgIcon-root": {
                                                    fontSize: 14,
                                                  },
                                                }}
                                              />
                                              <Typography
                                                sx={{
                                                  fontSize: 12,
                                                  color: "#5a6a74",
                                                  lineHeight: 1.5,
                                                }}
                                              >
                                                {renderOptionText(
                                                  opt.option_text,
                                                )}
                                              </Typography>
                                            </Box>
                                          ))}
                                        </Box>
                                      )}
                                  </Box>

                                  <Box className="editor-question-actions">
                                    <IconButton
                                      onClick={() =>
                                        handleEditQuestion(sub.id, q)
                                      }
                                    >
                                      <EditSquareIcon className="editor-action-icon" />
                                    </IconButton>
                                    <IconButton
                                      onClick={() =>
                                        handleOpenDeleteQuestion(sub.id, q)
                                      }
                                    >
                                      <DeleteOutlinedIcon className="editor-action-icon" />
                                    </IconButton>
                                  </Box>
                                </Box>
                              ))}

                              {/* Add question button — only when below the 4-question limit */}
                              {sub.questions.length <
                                MAX_QUESTIONS_PER_SUBSECTION && (
                                <Button
                                  className="editor-add-questions-btn"
                                  onClick={() => handleOpenAddQuestion(sub.id)}
                                >
                                  <AddIcon
                                    sx={{ fontSize: 16, marginRight: "4px" }}
                                  />
                                  {sub.questions.filter(
                                    (q) => q.type === "objective",
                                  ).length < OBJECTIVE_QUESTIONS_COUNT
                                    ? "Add Objective Question"
                                    : "Add Reflection Question"}
                                </Button>
                              )}
                            </Box>
                          ) : (
                            /* Empty state when no questions */
                            <Box className="editor-empty-question-box">
                              <Button
                                className="editor-add-questions-btn"
                                onClick={() => handleOpenAddQuestion(sub.id)}
                              >
                                <AddIcon
                                  sx={{ fontSize: 16, marginRight: "4px" }}
                                />
                                Add Objective Question
                              </Button>
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                  );
                })}
            </Box>
          );
        })}

        {/* Add Section button */}
        {sectionsData.length > 0 && sectionsData.length < MAX_SECTIONS && (
          <Button
            className="editor-add-questions-btn"
            onClick={handleOpenAddSection}
            sx={{ alignSelf: "flex-start" }}
          >
            <AddIcon sx={{ fontSize: 16, marginRight: "4px" }} />
            Add Section
          </Button>
        )}
      </Box>

      {/* ===== Add / Edit Question Popup ===== */}
      <Dialog
        open={addQuestionOpen}
        onClose={handleCloseAddQuestion}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          className: "editor-dialog-paper",
        }}
      >
        <DialogContent className="editor-dialog-content">
          {/* Dynamic title */}
          <Typography className="editor-dialog-title">
            {editQuestionMode
              ? `Edit ${currentQuestionType === "objective" ? "Objective" : "Self-Reflection"} Question`
              : `Add ${currentQuestionType === "objective" ? "Objective" : "Self-Reflection"} Question`}
          </Typography>

          {/* ── Edit / Preview Tabs ── */}
          <Tabs
            value={dialogTab}
            onChange={(_, v) => setDialogTab(v)}
            sx={{
              mb: 2,
              minHeight: 36,
              borderBottom: "1px solid #e8eaed",
              "& .MuiTabs-indicator": { backgroundColor: "#263842" },
              "& .MuiTab-root": {
                minHeight: 36,
                fontSize: 12,
                fontWeight: 600,
                textTransform: "none",
                color: "#9ca3af",
                px: 2,
                py: 0,
              },
              "& .Mui-selected": { color: "#263842 !important" },
            }}
          >
            <Tab label="Edit" />
            <Tab label="Preview" />
          </Tabs>

          {/* ── EDIT TAB ── */}
          {dialogTab === 0 && (
            <>
              {/* Objective fields */}
              {currentQuestionType === "objective" && (
                <>
                  <Typography className="editor-dialog-label">
                    Objective Question
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Type here"
                    variant="outlined"
                    size="small"
                    value={questionForm.question_text}
                    onChange={handleFormChange("question_text")}
                    className="editor-dialog-input"
                  />

                  {/* Likert label + shared B/I/U toolbar on the same row */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mt: "12px",
                      mb: "6px",
                    }}
                  >
                    <Typography className="editor-dialog-label">
                      Likert scale options
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        gap: "4px",
                        border: "1px solid #e0e0e0",
                        borderRadius: "6px",
                        p: "3px",
                        bgcolor: "#f5f5f5",
                      }}
                    >
                      {[
                        { icon: <FormatBoldIcon sx={{ fontSize: 15 }} />, label: "Bold", handler: handleBoldOption },
                        { icon: <FormatItalicIcon sx={{ fontSize: 15 }} />, label: "Italic", handler: handleItalicOption },
                        { icon: <FormatUnderlinedIcon sx={{ fontSize: 15 }} />, label: "Underline", handler: handleUnderlineOption },
                      ].map(({ icon, label, handler }) => (
                        <IconButton
                          key={label}
                          onMouseDown={(e) => {
                            e.preventDefault(); // keep textarea focus + selection intact
                            handler();
                          }}
                          size="small"
                          title={label}
                          sx={{
                            borderRadius: "4px",
                            p: "3px 6px",
                            color: "#263842",
                            "&:hover": { bgcolor: "#e0e0e0" },
                          }}
                        >
                          {icon}
                        </IconButton>
                      ))}
                    </Box>
                  </Box>
                  <Typography
                    sx={{ fontSize: 10, color: "#aaa", mb: 1, lineHeight: 1.4 }}
                  >
                    Click an option, select text, then use B / I / U above to format.
                  </Typography>

                  {questionForm.options.map((option, index) => (
                    <Box
                      key={index}
                      className="editor-dialog-likert-row"
                      sx={{ alignItems: "flex-start" }}
                    >
                      <Typography
                        className="editor-dialog-likert-number"
                        sx={{ mt: "6px" }}
                      >
                        {index + 1}
                      </Typography>

                      <TextField
                        fullWidth
                        placeholder="Type here"
                        variant="outlined"
                        size="small"
                        multiline
                        minRows={1}
                        maxRows={4}
                        value={option.option_text}
                        onChange={handleOptionChange(index)}
                        onFocus={() => setFocusedOptionIndex(index)}
                        className="editor-dialog-input"
                        inputRef={(el) => {
                          optionRefs.current[index] = el;
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            alignItems: "flex-start",
                          },
                          "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                            borderColor: "#009ccc",
                            borderWidth: "2px",
                          },
                        }}
                      />

                      {index >= 5 && (
                        <IconButton
                          onClick={() => handleRemoveOption(index)}
                          size="small"
                        >
                          <CloseIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      )}
                    </Box>
                  ))}

                  {questionForm.options.length < 5 && (
                    <Button
                      className="editor-add-subsection-btn"
                      onClick={handleAddOption}
                    >
                      <AddIcon sx={{ fontSize: 16, marginRight: "4px" }} />
                      Add Option
                    </Button>
                  )}
                </>
              )}

              {/* Reflection field */}
              {currentQuestionType === "reflection" && (
                <>
                  <Typography className="editor-dialog-label editor-dialog-label-reflection">
                    Self-Reflection Question
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Type here"
                    variant="outlined"
                    size="small"
                    value={questionForm.question_text}
                    onChange={handleFormChange("question_text")}
                    className="editor-dialog-input"
                  />
                </>
              )}
            </>
          )}

          {/* ── PREVIEW TAB ── */}
          {dialogTab === 1 && (
            <Box
              sx={{
                bgcolor: "#f8f9fa",
                border: "1px solid #e8eaed",
                borderRadius: "8px",
                p: 2.5,
              }}
            >
              {/* Question text */}
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#263842",
                  mb: 2,
                  lineHeight: 1.5,
                }}
              >
                {questionForm.question_text ? (
                  questionForm.question_text
                ) : (
                  <span
                    style={{
                      color: "#bbb",
                      fontStyle: "italic",
                      fontWeight: 400,
                    }}
                  >
                    Question text will appear here...
                  </span>
                )}
              </Typography>

              {/* Objective options preview */}
              {currentQuestionType === "objective" && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
                  {questionForm.options.filter((opt) => opt.option_text.trim())
                    .length > 0 ? (
                    questionForm.options
                      .filter((opt) => opt.option_text.trim())
                      .map((opt, i) => (
                        <Box
                          key={i}
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1,
                          }}
                        >
                          <Radio
                            size="small"
                            disabled
                            sx={{
                              color: "#90a4ae",
                              p: 0,
                              mt: "2px",
                              flexShrink: 0,
                              "& .MuiSvgIcon-root": { fontSize: 16 },
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: 13,
                              color: "#263842",
                              lineHeight: 1.5,
                            }}
                          >
                            {renderOptionText(opt.option_text)}
                          </Typography>
                        </Box>
                      ))
                  ) : (
                    <Typography
                      sx={{ fontSize: 12, color: "#bbb", fontStyle: "italic" }}
                    >
                      Options will appear here once you type them...
                    </Typography>
                  )}
                </Box>
              )}

              {/* Reflection preview */}
              {currentQuestionType === "reflection" && (
                <>
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "#009ccc",
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Leader Self-Reflection Question:
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: "#fff",
                      border: "1px dashed #ccc",
                      borderRadius: "4px",
                      p: 1.5,
                      minHeight: 60,
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 12, color: "#aaa", fontStyle: "italic" }}
                    >
                      Response textarea...
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 0.5, mt: 1 }}>
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Typography key={s} sx={{ fontSize: 18, color: "#ccc" }}>
                        ★
                      </Typography>
                    ))}
                  </Box>
                  <Typography sx={{ fontSize: 11, color: "#aaa" }}>
                    Self rating
                  </Typography>
                </>
              )}
            </Box>
          )}

          {/* Action buttons */}
          <Box className="editor-dialog-actions">
            <Button
              className="editor-dialog-cancel-btn"
              onClick={handleCloseAddQuestion}
            >
              CANCEL
            </Button>
            <Button
              className="editor-dialog-save-btn"
              variant="contained"
              onClick={handleSaveQuestion}
              disabled={savingQuestion}
              sx={{ minWidth: 140, position: "relative" }}
            >
              {savingQuestion ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                "SAVE QUESTION"
              )}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ===== Add Section Popup ===== */}
      <Dialog
        open={addSectionOpen}
        onClose={handleCloseAddSection}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          className: "editor-dialog-paper",
        }}
      >
        <DialogContent className="editor-dialog-content">
          <Typography className="editor-dialog-title">
            {editMode ? "Edit Section" : "Add Section"}
          </Typography>

          <Typography className="editor-dialog-label">Section Name</Typography>
          <TextField
            fullWidth
            placeholder="Enter section name"
            variant="outlined"
            size="small"
            value={sectionForm.sectionName}
            onChange={handleSectionFormChange("sectionName")}
            className="editor-dialog-input"
          />

          <Typography
            className="editor-dialog-label"
            sx={{ marginTop: "20px !important" }}
          >
            Subsections
          </Typography>

          {sectionForm.subsections.map((sub, index) => (
            <Box key={index} className="editor-dialog-subsection-row">
              <Typography className="editor-dialog-likert-number">
                {index + 1}
              </Typography>
              <TextField
                fullWidth
                placeholder={`Subsection ${index + 1} name`}
                variant="outlined"
                size="small"
                value={sub.name}
                onChange={handleSubsectionChange(index)}
                className="editor-dialog-input"
              />
              {index >= 3 && (
                <IconButton
                  onClick={() => handleRemoveSubsection(index)}
                  size="small"
                  className="editor-dialog-remove-btn"
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              )}
            </Box>
          ))}

          <Button
            className="editor-add-subsection-btn"
            onClick={handleAddSubsection}
          >
            <AddIcon sx={{ fontSize: 16, marginRight: "4px" }} />
            Add Subsection
          </Button>

          <Box className="editor-dialog-actions">
            <Button
              className="editor-dialog-cancel-btn"
              onClick={handleCloseAddSection}
            >
              CANCEL
            </Button>
            <Button
              className="editor-dialog-save-btn"
              variant="contained"
              onClick={handleSubmitSection}
              disabled={submittingSection}
              sx={{ minWidth: 100, position: "relative" }}
            >
              {submittingSection ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                "SUBMIT"
              )}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ===== Delete Section Confirmation Popup ===== */}
      <Dialog
        open={deleteOpen}
        onClose={handleCloseDelete}
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: "12px",
            padding: "24px",
            textAlign: "center",
          },
        }}
      >
        <DialogContent>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "18px",
              marginBottom: "10px",
            }}
          >
            Are you sure you want to delete this SECTION?
          </Typography>

          {deleteSection && (
            <>
              <Typography sx={{ fontSize: "14px", color: "#666" }}>
                It contains "{deleteSection.subsections.length} SUBSECTIONS" & "
                {deleteSection.subsections.reduce(
                  (acc, s) => acc + s.questions.length,
                  0,
                )}{" "}
                QUESTIONS".
              </Typography>

              <Typography
                sx={{ fontSize: "14px", color: "#999", marginTop: "6px" }}
              >
                This action cannot be undone.
              </Typography>
            </>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: "12px",
              marginTop: "24px",
            }}
          >
            <Button
              onClick={handleCloseDelete}
              sx={{
                border: "1px solid #ccc",
                color: "#333",
                padding: "6px 18px",
                borderRadius: "8px",
              }}
            >
              CANCEL
            </Button>

            <Button
              onClick={handleConfirmDelete}
              disabled={deletingSection}
              sx={{
                backgroundColor: "#E53935",
                color: "#fff",
                padding: "6px 18px",
                borderRadius: "8px",
                minWidth: 100,
                "&:hover": {
                  backgroundColor: "#d32f2f",
                },
              }}
            >
              {deletingSection ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                "CONFIRM"
              )}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ===== Delete Question Confirmation Popup ===== */}
      <Dialog
        open={deleteQuestionOpen}
        onClose={handleCloseDeleteQuestion}
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: "12px",
            padding: "24px",
            textAlign: "center",
          },
        }}
      >
        <DialogContent>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "18px",
              marginBottom: "10px",
            }}
          >
            Are you sure you want to delete this QUESTION?
          </Typography>

          <Typography sx={{ fontSize: "14px", color: "#666" }}>
            This will delete the{" "}
            {selectedQuestionToDelete?.question?.type === "objective"
              ? "Objective"
              : "Self-Reflection"}{" "}
            question permanently.
          </Typography>

          <Typography
            sx={{ fontSize: "14px", color: "#999", marginTop: "6px" }}
          >
            This action cannot be undone.
          </Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: "12px",
              marginTop: "24px",
            }}
          >
            <Button
              onClick={handleCloseDeleteQuestion}
              sx={{
                border: "1px solid #ccc",
                color: "#333",
                padding: "6px 18px",
                borderRadius: "8px",
              }}
            >
              CANCEL
            </Button>

            <Button
              onClick={handleConfirmDeleteQuestion}
              disabled={deletingQuestion}
              sx={{
                backgroundColor: "#E53935",
                color: "#fff",
                padding: "6px 18px",
                borderRadius: "8px",
                minWidth: 100,
                "&:hover": {
                  backgroundColor: "#d32f2f",
                },
              }}
            >
              {deletingQuestion ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                "CONFIRM"
              )}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <SnackbarAlert
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleCloseSnackbar}
      />
    </Box>
  );
};

export default QuestionEditor;
